import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/serviceMedia";
import { mediaPreviewUrl, mediaPublicUrl } from "@/lib/mediaUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const MAX_VIDEO_BYTES = 70 * 1024 * 1024;
const ACCEPT = "video/mp4,video/webm,video/quicktime,video/x-m4v,.mp4,.webm,.mov,.m4v";

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Blog (and other) video upload field. Stores a public media URL string.
 * Max size: 70MB (enforced client-side and on the API).
 */
export default function MediaVideoField({
  value = "",
  onChange,
  folder = "blog",
  label,
  hint,
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const fileRef = useRef(null);
  const url = typeof value === "string" ? value : (value?.src || "");
  const displayUrl = resolveMediaUrl(url) || url;

  const upload = async (file) => {
    if (!file) return;
    if (!String(file.type || "").startsWith("video/") && !/\.(mp4|webm|mov|m4v)$/i.test(file.name || "")) {
      toast.error("Please choose a video file (MP4, WebM, or MOV)");
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error(`Video too large (${formatBytes(file.size)}). Max is 70MB.`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", folder);
      const { data } = await api.post("/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 10 * 60 * 1000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      onChange(mediaPublicUrl(data));
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Video upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-2">
      {label && <Label className="text-xs uppercase tracking-wider text-white/50">{label}</Label>}
      {hint && <p className="text-[11px] text-white/40">{hint}</p>}

      {displayUrl ? (
        <div className="relative group rounded-lg overflow-hidden border border-white/10 bg-black/40">
          <video
            src={displayUrl}
            controls
            playsInline
            preload="metadata"
            className="w-full max-h-56 bg-black"
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Remove video"
          >
            <Icons.X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : null}

      <div
        className={`flex flex-col gap-2 p-3 rounded-lg border border-dashed transition-colors ${
          dragOver ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/20"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          upload(e.dataTransfer.files?.[0]);
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            className="btn-gradient text-white border-0 h-8"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            <Icons.Upload className="w-3.5 h-3.5 mr-1.5" />
            {uploading ? `Uploading… ${progress}%` : "Upload video"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-white/80 h-8 border border-white/10"
            onClick={() => setLibraryOpen(true)}
          >
            <Icons.FolderOpen className="w-3.5 h-3.5 mr-1.5" />
            Choose from library
          </Button>
        </div>
        <p className="text-[11px] text-white/40">
          MP4, WebM, or MOV · max 70MB. Drop a file here or pick from the media library.
        </p>
        {uploading && (
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-cyan-400/80 transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      <button
        type="button"
        className="text-[11px] text-white/40 hover:text-white/70"
        onClick={() => setShowUrl((v) => !v)}
      >
        {showUrl ? "Hide URL field" : "Advanced: paste video URL"}
      </button>
      {showUrl && (
        <Input
          value={url}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or /api/media/public/…"
          className="bg-black/20 border-white/10 text-white placeholder:text-white/30 text-xs"
        />
      )}

      <VideoLibraryDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        onSelect={(selectedUrl) => {
          onChange(selectedUrl);
          setLibraryOpen(false);
          toast.success("Video selected");
        }}
      />
    </div>
  );
}

function VideoLibraryDialog({ open, onOpenChange, onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get("/media", { params: { type: "video", page_size: 60, folder: "all" } })
      .then((r) => {
        if (!cancelled) setItems(r.data.items || r.data || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const filtered = items.filter((it) => {
    if (!q.trim()) return true;
    const hay = `${it.original_filename || ""} ${it.alt_text || ""} ${it.folder || ""}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 text-white sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Video library</DialogTitle>
          <DialogDescription className="text-white/50">
            Pick an existing video, or upload new files above / from the Media page.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by filename…"
          className="bg-black/20 border-white/10 text-white"
        />
        <div className="flex-1 overflow-y-auto min-h-[240px]">
          {loading ? (
            <p className="text-sm text-white/40 p-4">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-white/40 p-4">No videos found yet. Upload one above.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-1">
              {filtered.map((it) => {
                const src = mediaPreviewUrl(it) || mediaPublicUrl(it);
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => onSelect(mediaPublicUrl(it))}
                    className="rounded-lg overflow-hidden border border-white/10 hover:border-cyan-400/50 text-left bg-black/40"
                    title={it.original_filename || it.id}
                  >
                    <div className="aspect-video bg-black flex items-center justify-center relative">
                      {src ? (
                        <video src={src} muted preload="metadata" className="w-full h-full object-cover" />
                      ) : (
                        <Icons.Film className="w-8 h-8 text-white/40" />
                      )}
                      <span className="absolute bottom-1 right-1 text-[10px] px-1.5 py-0.5 rounded bg-black/70 text-white/80">
                        {formatBytes(it.size)}
                      </span>
                    </div>
                    <div className="px-2 py-1.5 text-[11px] text-white/70 truncate">
                      {it.original_filename || it.filename || "video"}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
